import {
  Avatar,
  Heading,
  Grid,
  Flex,
  Tooltip,
  Text,
  Link,
  useDisclosure,
  IconButton,
  Collapse,
  List,
  ListItem,
  useColorModeValue,
} from '@chakra-ui/react'
import { FC } from 'react'
import { FaGithub, FaCodeBranch, FaCaretRight } from 'react-icons/fa'
import Console from './Console'
import { gql, useQuery } from '@apollo/client'

import DeploymentStatus from './DeploymentStatus'

type Props = {
  repoUrl: string
  deployment: {
    id: string
    status: string
    buildTime: number
    commitSha: string
    commitMessage: string
    branch: string
    createdAt: Date
    authorLogin: string
    authorImg: string
  }
}

const DEPLOYMENT_LOG_QUERY = gql`
  query DeploymentLog($idDeployment: String!) {
    deployment(id: $idDeployment) {
      id
      log
    }
  }
`

const Deployment: FC<Props> = ({ repoUrl = '', deployment }) => {
  const { isOpen, onToggle } = useDisclosure()
  const { data, loading: isLogLoading } = useQuery(DEPLOYMENT_LOG_QUERY, {
    variables: { idDeployment: deployment?.id },
    pollInterval: isOpen ? 3000 : undefined,
    skip: !isOpen,
  })
  const listItemBg = useColorModeValue('gray.50', 'gray.700')
  const listItemBgHover = useColorModeValue('gray.100', 'rgba(45, 55, 72, 0.8)')

  const logs = data?.deployment?.log ?? ''

  return (
    <div>
      <Grid
        key={deployment.id}
        gridTemplateColumns="200px 2fr 1fr 40px"
        gap={2}
      >
        {/* @ts-ignore */}
        <DeploymentStatus status={deployment?.status} />
        <Flex alignItems="center">
          <Flex alignItems="center" mr={4}>
            <FaGithub />
            <Link ml={2} href={repoUrl} rel="noreferrer" target="_blank">
              {repoUrl.replace('https://github.com/', '')}
            </Link>
          </Flex>
          <div>
            <Tooltip
              label={`Commit ${deployment?.commitSha?.slice(0, 6) ?? 'N/A'}`}
              placement="top"
            >
              <Flex alignItems="center" w="min-content">
                <FaCodeBranch />
                <Link
                  href={`${repoUrl}/commit/${deployment?.commitSha ?? ''}`}
                  rel="noreferrer"
                  target="_blank"
                  ml={2}
                >
                  {deployment?.branch ?? 'N/A'}
                </Link>
              </Flex>
            </Tooltip>
          </div>
        </Flex>
        <Text align="right" alignSelf="center" mr="1">
          {new Intl.DateTimeFormat('en-US', {
            timeStyle: 'short',
            dateStyle: 'medium',
          }).format(new Date(deployment.createdAt))}
        </Text>
        <IconButton
          variant="ghost"
          aria-label="Expand deployment details"
          icon={
            <FaCaretRight
              style={{
                transform: `rotate(${isOpen ? '90deg' : 0})`,
                transition: '150ms transform',
              }}
            />
          }
          onClick={onToggle}
        />
      </Grid>
      <Collapse in={isOpen} animateOpacity>
        <Grid pt={4} gridTemplateColumns="0.7fr 0.3fr" columnGap={8} rowGap={4}>
          <Heading as="h3" size="md">
            Logs
          </Heading>
          <Heading as="h3" size="md">
            Details
          </Heading>
          <Console isLoading={isLogLoading} logs={logs} h={400} />
          <List spacing={6}>
            <ListItem
              bgColor={listItemBg}
              rounded="md"
              p={4}
              _hover={{ bgColor: listItemBgHover }}
            >
              <Flex justifyContent="space-between">
                <b>Build time:</b>
                <Text>{calculateBuildTime(deployment.buildTime) || 'N/A'}</Text>
              </Flex>
            </ListItem>
            <ListItem
              bgColor={listItemBg}
              rounded="md"
              p={4}
              _hover={{ bgColor: listItemBgHover }}
            >
              <Flex justifyContent="space-between">
                <b>Commit author:</b>
                <Link
                  href={`https://github.com/${deployment.authorLogin}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Flex alignItems="center">
                    <Avatar
                      size="xs"
                      name={deployment.authorLogin}
                      src={deployment.authorImg}
                      mr={2}
                    />
                    <Text>{deployment.authorLogin}</Text>
                  </Flex>
                </Link>
              </Flex>
            </ListItem>
            <ListItem
              bgColor={listItemBg}
              rounded="md"
              p={4}
              _hover={{ bgColor: listItemBgHover }}
            >
              <Text as="b">Commit message:</Text>
              <Text as="code" display="block" mt={1} fontSize="sm">
                {deployment.commitMessage}
              </Text>
            </ListItem>
          </List>
        </Grid>
      </Collapse>
    </div>
  )
}

export default Deployment

const calculateBuildTime = (diff: number): string => {
  const SECOND = 1000
  const MINUTE = 60 * SECOND
  const HOUR = 60 * MINUTE

  let remaining = diff

  const hours = Math.floor(remaining / HOUR)
  remaining = hours > 0 ? remaining - hours * HOUR : remaining

  const minutes = Math.floor(remaining / MINUTE)
  remaining = minutes > 0 ? remaining - minutes * MINUTE : remaining

  const seconds = Math.floor(remaining / SECOND)

  const buildTime = `${hours > 0 ? `${hours}h` : ''} ${
    minutes > 0 ? `${minutes}m` : ''
  } ${seconds > 0 ? `${seconds}s` : ''}`

  return buildTime.trim()
}
